/**
 * Display-only translation lookup for DRUG NAMES.
 *
 * Two tiers, human-curated always wins:
 *   1. entity_translations (entityType='drugs', fieldName='name') — an admin explicitly typed
 *      and saved this translation (same mechanism as MasterDataPage.tsx master-data names).
 *      This is the ONLY tier `getDrugDisplayName` below uses, since it must return a value
 *      synchronously with no network call.
 *   2. LibreTranslate, cached (see utils/autoTranslate.ts + AutoTranslatedText component) — used
 *      as a fallback wherever a drug has no manual translation yet. This is genuine machine
 *      translation, done at the user's explicit request despite the patient-safety trade-off
 *      that was flagged and acknowledged: an auto-translated drug name is never 100% guaranteed
 *      accurate the way a human-verified one is. Callers that want this second tier should use
 *      <AutoTranslatedText text={original} manual={getManualDrugTranslation(...)} /> instead of
 *      calling getDrugDisplayName directly.
 *
 * Nothing about what's SAVED changes either way: onSelect/onChange handlers in DrugSearchInput
 * still receive the drug's original `name`/`drugName` from the API, so prescriptions, CPOE
 * orders, and discharge medication records are always written with the canonical drug name —
 * only the on-screen label can show a translated name.
 */
import { useState, useEffect } from 'react';
import api from '../services/api';

let cache: Record<string, Record<string, string>> | null = null;
let cachePromise: Promise<Record<string, Record<string, string>>> | null = null;

async function fetchDrugTranslations(): Promise<Record<string, Record<string, string>>> {
  if (cache) return cache;
  if (!cachePromise) {
    cachePromise = api.getTranslations('drugs').then((r: any) => {
      const rows: any[] = r?.data || [];
      const grouped: Record<string, Record<string, string>> = {};
      for (const row of rows) {
        if (row.fieldName !== 'name') continue;
        if (!grouped[row.entityId]) grouped[row.entityId] = {};
        grouped[row.entityId][row.languageCode] = row.value;
      }
      cache = grouped;
      return grouped;
    }).catch(() => {
      cache = {};
      return {};
    });
  }
  return cachePromise;
}

/** React hook: returns the loaded translations map (entityId -> languageCode -> value). */
export function useDrugTranslations(): Record<string, Record<string, string>> {
  const [map, setMap] = useState<Record<string, Record<string, string>>>(cache || {});
  useEffect(() => {
    let alive = true;
    fetchDrugTranslations().then((m) => { if (alive) setMap(m); });
    return () => { alive = false; };
  }, []);
  return map;
}

/**
 * Resolve the display name for a drug given the current UI language.
 * `drug` needs an `id` (the formulary drug id) plus a `name`/`drugName` fallback.
 * If no curated translation exists for this drug+language, returns the original name untouched.
 */
export function getDrugDisplayName(
  drug: { id?: string; drugId?: string; name?: string; drugName?: string } | null | undefined,
  lang: string,
  translations: Record<string, Record<string, string>>,
): string {
  if (!drug) return '';
  const original = drug.name || drug.drugName || '';
  const id = drug.id || drug.drugId;
  const langCode = (lang || 'en').split('-')[0];
  if (langCode === 'en' || !id) return original;
  return translations[id]?.[langCode] || original;
}

/**
 * Same lookup as getDrugDisplayName, but returns undefined (rather than falling back to the
 * original name) when no manual translation exists — so a caller can tell "no manual
 * translation" apart from "translation happens to equal the original text", and decide to try
 * the auto-translate fallback in that case (see AutoTranslatedText).
 */
export function getManualDrugTranslation(
  drug: { id?: string; drugId?: string } | null | undefined,
  lang: string,
  translations: Record<string, Record<string, string>>,
): string | undefined {
  if (!drug) return undefined;
  const id = drug.id || drug.drugId;
  const langCode = (lang || 'en').split('-')[0];
  if (langCode === 'en' || !id) return undefined;
  return translations[id]?.[langCode];
}
